import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  Scale, 
  FileText, 
  Briefcase, 
  Bell, 
  Newspaper, 
  ChevronRight,
  ChevronLeft,
  Target,
  Heart,
  Gavel,
  Clock,
  Users,
  Building2,
  MapPin,
  Phone,
  Mail,
  Calendar
} from 'lucide-react';
import { 
  resolutionsApi, 
  ordinancesApi, 
  vacanciesApi, 
  announcementsApi, 
  newsApi,
  calendarApi
} from '../services/api';

// Hardcoded system information
const WELCOME_TITLE = 'Welcome to Sangguniang Bayan Transparency Website';
const WELCOME_SUBTITLE = 'Municipal Compound, San Francisco, Southern Leyte, 6613 Philippines';
const WELCOME_MESSAGE = 'Transparency in Governance, Service to the People';

// Hero carousel images from homepage-images folder
const HERO_IMAGES = [
  '/homepage-images/hero-bg.jpg',
  '/homepage-images/hero-bg2.jpg',
  '/homepage-images/hero-bg3.jpg'
];

// Hardcoded statistics - default values
const DEFAULT_STATS = { resolutions: 0, ordinances: 0, yearsServing: 76, population: '12,900+' };

const HomePage: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [resolutions, setResolutions] = useState<any[]>([]);
  const [ordinances, setOrdinances] = useState<any[]>([]);
  const [vacancies, setVacancies] = useState<any[]>([]);
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [news, setNews] = useState<any[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(DEFAULT_STATS);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [resolutionsRes, ordinancesRes, vacanciesRes, announcementsRes, newsRes, allResolutions, allOrdinances, calendarRes] = await Promise.all([
        resolutionsApi.getAll({ limit: 5, status: 'Approved', isPublic: true }).catch(() => ({ resolutions: [], pagination: { totalItems: 0 } })),
        ordinancesApi.getAll({ limit: 5, status: 'Approved', isPublic: true }).catch(() => ({ ordinances: [], pagination: { totalItems: 0 } })),
        vacanciesApi.getAll().catch(() => ({ vacancies: [] })),
        announcementsApi.getAll().catch(() => ({ announcements: [] })),
        newsApi.getAll({ limit: 5 }).catch(() => ({ news: [] })),
        resolutionsApi.getAll({ status: 'Approved', isPublic: true, limit: 1 }).catch(() => ({ pagination: { totalItems: 0 } })),
        ordinancesApi.getAll({ status: 'Approved', isPublic: true, limit: 1 }).catch(() => ({ pagination: { totalItems: 0 } })),
        calendarApi.getPublic({ upcoming: true, limit: 5 }).catch(() => ({ events: [] }))
      ]);
      setResolutions(resolutionsRes.resolutions || []);
      setOrdinances(ordinancesRes.ordinances || []);
      setVacancies(vacanciesRes.vacancies || []);
      setAnnouncements(announcementsRes.announcements || []);
      setNews(newsRes.news || []);
      setCalendarEvents(calendarRes.events || []);
      // Update stats with actual counts from database
      setStats(prev => ({
        ...prev,
        resolutions: allResolutions.pagination?.totalItems || 0,
        ordinances: allOrdinances.pagination?.totalItems || 0
      }));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;

  return (
    <div className="space-y-0">
      {/* Hero Section with Image Carousel */}
      <section className="relative h-[400px] sm:h-[500px] md:h-[600px] overflow-hidden">
        {HERO_IMAGES.map((src, index) => (
          <div key={index} className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}>
            <img src={src} alt={`Hero ${index + 1}`} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-blue-900/80 via-blue-800/70 to-blue-900/80"></div>
          </div>
        ))}
        <div className="relative z-10 h-full flex flex-col items-center justify-center px-4">
          <div className="mb-4 sm:mb-6 bg-white/10 backdrop-blur-sm p-3 sm:p-4 rounded-full animate-fade-in-up">
            <img src="/homepage-images/logo.png" alt="Logo" className="h-20 w-20 sm:h-32 sm:w-32 md:h-48 md:w-48 object-contain animate-pulse-slow" />
          </div>
          <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-2 sm:mb-4 text-center drop-shadow-lg animate-fade-in-up animate-pulse-glow delay-200 px-4">{WELCOME_TITLE}</h1>
          <p className="text-base sm:text-xl md:text-2xl lg:text-3xl text-blue-100 mb-1 sm:mb-2 text-center drop-shadow-md animate-slide-in-left delay-400 px-4">{WELCOME_SUBTITLE}</p>
          <p className="text-sm sm:text-lg md:text-xl text-blue-200 text-center max-w-2xl drop-shadow-sm animate-slide-in-right delay-600 px-4">{WELCOME_MESSAGE}</p>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex space-x-2">
          {HERO_IMAGES.map((_, index) => (
            <button key={index} onClick={() => setCurrentSlide(index)} className={`w-3 h-3 rounded-full transition ${index === currentSlide ? 'bg-white' : 'bg-white/50'}`} />
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-blue-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            <div className="text-center text-white animate-fade-in-up"><Scale className="h-8 w-8 sm:h-10 sm:w-10 mx-auto text-blue-300 mb-2 sm:mb-3 animate-bounce-slow" /><p className="text-2xl sm:text-4xl font-bold animate-count-up">{stats.resolutions}+</p><p className="text-blue-200 mt-1 text-sm sm:text-base">Resolutions</p></div>
            <div className="text-center text-white animate-fade-in-up delay-100"><FileText className="h-8 w-8 sm:h-10 sm:w-10 mx-auto text-green-300 mb-2 sm:mb-3 animate-bounce-slow" /><p className="text-2xl sm:text-4xl font-bold animate-count-up">{stats.ordinances}+</p><p className="text-blue-200 mt-1 text-sm sm:text-base">Ordinances</p></div>
            <div className="text-center text-white animate-fade-in-up delay-200"><Clock className="h-8 w-8 sm:h-10 sm:w-10 mx-auto text-yellow-300 mb-2 sm:mb-3 animate-bounce-slow" /><p className="text-2xl sm:text-4xl font-bold">{stats.yearsServing}</p><p className="text-blue-200 mt-1 text-sm sm:text-base">Years Serving</p></div>
            <div className="text-center text-white animate-fade-in-up delay-300"><Users className="h-8 w-8 sm:h-10 sm:w-10 mx-auto text-purple-300 mb-2 sm:mb-3 animate-bounce-slow" /><p className="text-2xl sm:text-4xl font-bold">{stats.population}</p><p className="text-blue-200 mt-1 text-sm sm:text-base">Population Served</p></div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-green-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">About Sangguniang Bayan</h2>
            <div className="w-32 h-1.5 bg-gradient-to-r from-blue-600 to-green-600 mx-auto rounded-full"></div>
            <p className="mt-6 text-lg text-gray-600 max-w-3xl mx-auto">Dedicated to serving the people of San Francisco, Southern Leyte with transparency and excellence</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div className="group">
              <div className="bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Target className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-center mb-4 text-gray-900">Our Mission</h3>
                <p className="text-gray-600 text-center leading-relaxed text-lg font-medium">"TO ENACT QUALITY LEGISLATION AND EXERCISE EFFECTIVE OVERSIGHT THAT PROMOTES SOCIAL JUSTICE, ECONOMIC GROWTH, AND ENVIRONMENTAL SUSTAINABILITY THROUGH INCLUSIVE, TRANSPARENT, AND PARTICIPATORY GOVERNANCE"</p>
              </div>
            </div>
            <div className="group">
              <div className="bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
                <div className="bg-gradient-to-br from-green-500 to-green-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <Heart className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-center mb-4 text-gray-900">Our Vision</h3>
                <p className="text-gray-600 text-center leading-relaxed text-lg font-medium">"A PREMIER AND PROACTIVE LEGISLATIVE BODY IN SOUTHERN LEYTE, ENACTING RESPONSIVE AND INNOVATIVE POLICIES THAT EMPOWER A RESILIENT, GOD-LOVING, AND PROSPEROUS SAN FRANCISCO"</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Official Seal Section */}
      <section className="bg-gradient-to-br from-purple-50 via-white to-indigo-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">The Official Seal of Sangguniang Bayan</h2>
            <div className="w-32 h-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 mx-auto rounded-full"></div>
            <p className="mt-6 text-lg text-gray-600 max-w-3xl mx-auto">Symbolism of the Official Seal: Sangguniang Bayan of San Francisco, Southern Leyte</p>
          </div>
          
          {/* Official Seal and its text - Centered */}
          <div className="flex flex-col items-center mb-16">
            <div className="bg-white rounded-full shadow-2xl p-8 transition-all duration-300 hover:shadow-3xl hover:scale-105">
              <img 
                src="/homepage-images/logo.png" 
                alt="Official Seal of Sangguniang Bayan" 
                className="h-64 w-64 lg:h-80 lg:w-80 object-contain"
              />
            </div>
            <div className="mt-8 text-center space-y-1">
              <p className="text-base font-bold text-gray-700 tracking-wider">SANGGUNIANG BAYAN</p>
              <p className="text-sm font-semibold text-gray-600">SAN FRANCISCO, SOUTHERN LEYTE</p>
              <div className="flex items-center justify-center space-x-2 mt-2">
                <span className="text-xs text-gray-500">1951</span>
              </div>
            </div>
          </div>

          {/* Seal Information - Stacked below seal */}
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="bg-gradient-to-br from-purple-500 to-indigo-600 w-10 h-10 rounded-lg flex items-center justify-center mr-3">
                  <Gavel className="h-6 w-6 text-white" />
                </div>
                The Scale and the Quill
              </h3>
              <p className="text-sm font-semibold text-purple-600 mb-2">The Legislative Mandate</p>
              <p className="text-gray-600 leading-relaxed">The Scale represents the unwavering commitment of the Council to social justice and equality, signifying that every ordinance is weighed with fairness. The Quill represents the power of legislation and the intellectual labor of the members in crafting laws that protect the welfare of the people.</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="bg-gradient-to-br from-yellow-500 to-orange-600 w-10 h-10 rounded-lg flex items-center justify-center mr-3">
                  <Target className="h-6 w-6 text-white" />
                </div>
                The Sun and the Gear
              </h3>
              <p className="text-sm font-semibold text-orange-600 mb-2">Progress and Unity</p>
              <p className="text-gray-600 leading-relaxed">The Sun symbolizes the distinct barangays of San Francisco, unified under a single vision of hope and transparency. The integrated Gear reflects the Council's role as the engine of progress, driving the municipality toward modernization and economic growth.</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="bg-gradient-to-br from-green-500 to-teal-600 w-10 h-10 rounded-lg flex items-center justify-center mr-3">
                  <Heart className="h-6 w-6 text-white" />
                </div>
                The Coconut Fronds and Waves
              </h3>
              <p className="text-sm font-semibold text-green-600 mb-2">Geography and Heritage</p>
              <p className="text-gray-600 leading-relaxed">The Coconut Fronds pay homage to the "Tree of Life," representing the agricultural roots and the resilience of the San Franciscanos people. The Waves signify the town's coastal identity and the life-giving waters of Sogod Bay.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                  <FileText className="h-5 w-5 text-blue-600 mr-2" />
                  The Open Book
                </h4>
                <p className="text-sm font-semibold text-blue-600 mb-2">Transparency and Law</p>
                <p className="text-gray-600 text-sm leading-relaxed">Represents the Local Government Code and the Constitution, ensuring all legislative actions are rooted in the rule of law and remain open to the public.</p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
                <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                  <Scale className="h-5 w-5 text-purple-600 mr-2" />
                  Navy and Gold Palette
                </h4>
                <p className="text-sm font-semibold text-purple-600 mb-2">Authority and Excellence</p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  <span className="block mb-1"><strong>Navy Blue:</strong> Commitment, stability, and formal authority</span>
                  <span><strong>Gold:</strong> Excellence, prosperity, and premier status</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Documents Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="space-y-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900 flex items-center"><Scale className="h-6 w-6 mr-2 text-blue-600" />Recent Resolutions</h3>
              <Link to="/resolutions" className="text-blue-600 hover:text-blue-800 flex items-center text-sm font-medium">View All <ChevronRight className="h-4 w-4 ml-1" /></Link>
            </div>
            <div className="space-y-4">
              {resolutions.length > 0 ? resolutions.map((res) => (
                <div key={res.id} className="border-l-4 border-blue-500 pl-4 py-3 bg-gray-50 rounded-r-lg hover:bg-blue-50 transition-colors">
                  <p className="text-sm text-blue-600 font-medium">Resolution No. {res.resolutionNumber}, Series {res.series}</p>
                  <p className="font-medium text-gray-900 line-clamp-2">{res.title}</p>
                </div>
              )) : <p className="text-gray-500 text-center py-8">No resolutions available.</p>}
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900 flex items-center"><FileText className="h-6 w-6 mr-2 text-green-600" />Recent Ordinances</h3>
              <Link to="/ordinances" className="text-green-600 hover:text-green-800 flex items-center text-sm font-medium">View All <ChevronRight className="h-4 w-4 ml-1" /></Link>
            </div>
            <div className="space-y-4">
              {ordinances.length > 0 ? ordinances.map((ord) => (
                <div key={ord.id} className="border-l-4 border-green-500 pl-4 py-3 bg-gray-50 rounded-r-lg hover:bg-green-50 transition-colors">
                  <p className="text-sm text-green-600 font-medium">Ordinance No. {ord.ordinanceNumber}, Series {ord.series}</p>
                  <p className="font-medium text-gray-900 line-clamp-2">{ord.title}</p>
                </div>
              )) : <p className="text-gray-500 text-center py-8">No ordinances available.</p>}
            </div>
          </div>
        </div>
      </section>

      {/* Job Vacancies */}
      {vacancies.length > 0 && (
        <section className="bg-gradient-to-r from-orange-500 to-red-500 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-white flex items-center"><Briefcase className="h-7 w-7 mr-3" />Job Opportunities</h2>
              <Link to="/vacancies" className="text-white/90 hover:text-white flex items-center text-sm font-medium bg-white/20 px-4 py-2 rounded-lg">View All <ChevronRight className="h-4 w-4 ml-1" /></Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
              {vacancies.slice(0, 5).map((vac) => (
                <div key={vac.id} className="bg-white/10 backdrop-blur rounded-xl p-6 hover:bg-white/20 transition-colors border border-white/20">
                  <p className="font-bold text-white text-lg">{vac.jobTitle}</p>
                  <p className="text-orange-100 mt-1">{vac.position}</p>
                  <p className="text-sm text-orange-200/80 mt-2">{vac.department}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Calendar of Activities */}
      {calendarEvents.length > 0 && (
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center"><Calendar className="h-7 w-7 mr-3 text-indigo-600" />Calendar of Activities</h2>
              <Link to="/calendar" className="text-indigo-600 hover:text-indigo-800 flex items-center text-sm font-medium">View Calendar <ChevronRight className="h-4 w-4 ml-1" /></Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {calendarEvents.slice(0, 3).map((event) => (
                <div key={event.id} className="bg-indigo-50 rounded-xl p-6 border-l-4 border-indigo-500 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded">{event.eventType}</span>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{event.title}</h3>
                  <p className="text-sm text-gray-600 mb-1">
                    {new Date(event.eventDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                  {event.location && <p className="text-sm text-gray-500">{event.location}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* News & Announcements */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center"><Newspaper className="h-7 w-7 mr-3 text-purple-600" />Latest News</h2>
                <Link to="/news" className="text-purple-600 hover:text-purple-800 flex items-center text-sm font-medium">View All <ChevronRight className="h-4 w-4 ml-1" /></Link>
              </div>
              <div className="space-y-4">
                {news.length > 0 ? news.map((item) => (
                  <div key={item.id} className="bg-white rounded-lg shadow p-5 hover:shadow-md transition-shadow border-l-4 border-purple-500">
                    <span className="inline-block px-3 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full mb-2">{item.category}</span>
                    <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2">{item.content}</p>
                  </div>
                )) : <p className="text-gray-500 text-center py-8">No news available.</p>}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center"><Bell className="h-7 w-7 mr-3 text-red-600" />Announcements</h2>
                <Link to="/announcements" className="text-red-600 hover:text-red-800 flex items-center text-sm font-medium">View All <ChevronRight className="h-4 w-4 ml-1" /></Link>
              </div>
              <div className="space-y-4">
                {announcements.length > 0 ? announcements.slice(0, 5).map((ann) => (
                  <div key={ann.id} className={`rounded-lg p-5 border-l-4 ${ann.priority === 'Urgent' ? 'bg-red-50 border-red-500' : ann.priority === 'High' ? 'bg-orange-50 border-orange-500' : 'bg-blue-50 border-blue-500'}`}>
                    <span className={`inline-block px-2 py-1 text-xs rounded mb-1 ${ann.priority === 'Urgent' ? 'bg-red-100 text-red-800' : ann.priority === 'High' ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'}`}>{ann.priority}</span>
                    <h3 className="font-bold text-gray-900">{ann.title}</h3>
                  </div>
                )) : <p className="text-gray-500 text-center py-8">No announcements available.</p>}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
